import { Box, Stack, Table, TableBody, TableCell, TableRow, Typography } from "@mui/material";

/**
 * BTS 전용 개인정보 수집·이용 동의서.
 *
 * ## 왜 외부 링크가 아니라 여기에 있나
 *
 * 기존 동의서(`terms.frism.io`)는 **투자자문 상담 기준**이라 우리가 받지도 않는
 * 것들이 적혀 있다 — 연소득·계좌정보·금융자산·재무상태·투자목적. 없는 것을 받는다고
 * 적은 동의서는 동의를 받은 것이 아니다.
 *
 * 그리고 그 페이지는 **SPA 라 모달에서 5초 넘게 흰 화면**이었다(실측: `load` 는
 * 1ms·40ms 에 뜨는데 글은 그보다 한참 뒤에 나온다). 로딩 표시로도 못 가린다 —
 * 언제 그려졌는지 밖에서는 알 수 없기 때문이다. 우리 화면에서 그리면 즉시 뜬다.
 *
 * ## 내용의 근거
 *
 * `us-api` `docs/bts-개인정보-동의서-초안.md` — DB 컬럼과 코드를 전수로 훑어
 * «실제로 저장되는 것» 만 적었다. **법무 검토 전 초안이다.** 확정되면 문구를
 * 여기서 한 번 고친다(약관 표가 서버에 생기면 그때 서버에서 받아 온다).
 */
const ROWS: { label: string; body: React.ReactNode }[] = [
  {
    label: "수집·이용 목적",
    body: (
      <>
        Back Test System 계정의 <b>식별 및 본인확인</b>
        <Box component="ul" sx={{ pl: 2.5, my: 0.5 }}>
          <li>로그인 링크 발송 및 로그인 처리</li>
          <li>계정을 잊었을 때 본인 확인 후 안내</li>
          <li>부정 이용(타인 명의 가입·로그인) 방지</li>
        </Box>
      </>
    ),
  },
  {
    label: "수집 항목",
    body: (
      <>
        <b>[필수]</b> 이름, 이메일 주소, <b>본인확인 식별값</b>
        <Box component="ul" sx={{ pl: 2.5, my: 0.5 }}>
          {/* 사용자가 알고 싶은 것은 «내 정보가 어디까지 남는가» 다. 우리 답이
              «거의 안 남는다» 이므로 숨길 이유가 없고, 먼저 말하는 편이 낫다. */}
          <li>
            <b>본인확인 식별값은 원본을 저장하지 않습니다.</b> 본인확인기관이 제공한
            연계정보(CI)를 되돌릴 수 없는 형태로 변환하여 보관하며, 이 값은 「같은
            사람인지」를 가리는 데에만 사용됩니다. 프리즘의 다른 서비스나 외부
            시스템의 정보와 <b>대조할 수 없습니다.</b>
          </li>
          <li>
            본인확인에 필요한 생년월일·성별·내외국인 구분·휴대폰번호·통신사는{" "}
            <b>입력받은 즉시 본인확인기관에 전달되며 회사에 저장되지 않습니다.</b>
          </li>
          <li>
            주민등록번호는 <b>앞 6자리와 뒤 1자리만</b> 입력받고, 나머지 6자리는{" "}
            <b>입력받지 않으며</b> 저장하지도 않습니다.
          </li>
        </Box>
      </>
    ),
  },
  {
    label: "보유·이용 기간",
    body: (
      <>
        회원 계정이 유지되는 기간
        <br />
        단, 관계 법령에 따라 보존이 필요한 경우 해당 기간 동안 분리 보관합니다.
      </>
    ),
  },
  {
    label: "동의를 거부할 권리 및 거부 시 불이익",
    body: (
      <>
        위 개인정보의 수집·이용 동의는 <b>서비스 이용에 필수적</b>입니다. 동의를
        거부하실 수 있으나, 거부하시는 경우 Back Test System 계정 생성 및 로그인이
        불가능합니다.
      </>
    ),
  },
];

export default function BtsPrivacyTerms() {
  return (
    <Stack spacing={1.5} sx={{ p: 3 }}>
      {/* 제목은 모달 머리(`DialogTitle`)가 이미 보여 준다. 여기서 또 쓰면 같은
          문장이 두 줄로 겹쳐 보인다. */}
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        [㈜프리즘투자자문]
      </Typography>
      <Typography variant="body2">
        회사가 본인의 개인정보를 수집·이용하고자 하는 경우에는 「개인정보보호법」에 따라
        본인의 동의를 얻어야 합니다. 이에 본인은 회사가 아래와 같이 개인정보를
        수집·이용하는 것에 동의합니다.
      </Typography>

      <Box
        sx={{
          bgcolor: "text.primary",
          color: "background.paper",
          textAlign: "center",
          py: 1,
          mt: 1,
        }}
      >
        <Typography variant="subtitle2">1. 수집 및 이용에 관한 사항</Typography>
      </Box>
      <Table size="small" sx={{ mt: "0 !important" }}>
        <TableBody>
          {ROWS.map((row) => (
            <TableRow key={row.label}>
              <TableCell
                sx={{
                  width: 160,
                  verticalAlign: "top",
                  textAlign: "center",
                  fontWeight: "fontWeightMedium",
                }}
              >
                {row.label}
              </TableCell>
              <TableCell sx={{ verticalAlign: "top" }}>
                <Typography variant="body2" component="div">
                  {row.body}
                </Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Stack>
  );
}
